const BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001/api/v1';

// Auth now lives in an httpOnly session cookie (set by POST /auth/login) —
// invisible to JS, so unlike the old localStorage token it can't be read or
// exfiltrated by an injected script. credentials:'include' is what makes the
// browser attach it; the backend's CORS config already allows it
// (credentials:true + an explicit origin allowlist, see backend app.ts).
async function adminRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  const json = await res.json();
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok || !json.success) throw new Error(json.error ?? `Request failed: ${res.status}`);
  return json.data as T;
}

// Separate from adminRequest: file uploads must be sent as multipart/form-data,
// so the Content-Type (with its boundary) has to be left for the browser to set —
// setting it manually, or reusing the JSON header above, breaks the upload.
async function adminUpload<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('image', file);

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    body,
  });

  const json = await res.json();
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok || !json.success) throw new Error(json.error ?? `Upload failed: ${res.status}`);
  return json.data as T;
}

// Separate from adminRequest: login/me/logout errors must keep their actual
// message (e.g. "Email ou mot de passe incorrect") instead of being
// collapsed into the generic 'UNAUTHORIZED' thrown above on any 401 — that
// shortcut exists so CRUD calls can redirect to the login screen on
// expiry, but it would hide the real reason a login attempt failed.
async function authRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });

  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error ?? `Request failed: ${res.status}`);
  return json.data as T;
}

export const adminAuthApi = {
  login: (email: string, password: string) =>
    authRequest<{ email: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => authRequest<null>('/auth/logout', { method: 'POST' }),
  me: () => authRequest<{ email: string }>('/auth/me'),
};

export interface OrderItem {
  id: string;
  product_id: string | null;
  product_name: string;
  quantity_kg: string;
  quantity_unit?: string | null;
  unit_price: string | null;
  total_price: string | null;
}

export interface OrderDetail {
  id: string;
  order_number: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string | null;
  customer_company?: string | null;
  customer_country?: string | null;
  status: string;
  total_amount: string | null;
  currency: string;
  shipping_address: Record<string, any> | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  items: OrderItem[];
}

export const adminOrdersApi = {
  getAll: (status?: string) =>
    adminRequest<any[]>(`/orders${status ? `?status=${status}` : ''}`),
  getOne: (id: string) => adminRequest<OrderDetail>(`/orders/${id}`),
  updateStatus: (id: string, status: string, notes?: string) =>
    adminRequest<any>(`/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    }),
  // Not fetched via adminRequest: the PDF is a binary response for a plain
  // <a>/window.open navigation, not a JSON fetch — the browser attaches the
  // httpOnly session cookie to that navigation on its own.
  pdfUrl: (id: string) => `${BASE_URL}/orders/${id}/pdf`,
};

export const adminQuotesApi = {
  getAll: () => adminRequest<any[]>('/quotes'),
  getOne: (id: string) => adminRequest<any>(`/quotes/${id}`),
  updateStatus: (id: string, status: string, notes?: string) =>
    adminRequest<any>(`/quotes/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    }),
};

export interface ProductSpec {
  name: string;
  value: string;
  unit?: string | null;
}

export interface ProductOrigin {
  region: string;
  country: string;
  altitude?: string | null;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  category: string;
  grade: string | null;
  image: string;
  images: string[];
  description_fr: string;
  description_en: string;
  certifications: string[];
  in_stock: boolean;
  min_order_kg: string | null;
  sort_order: number;
  specifications: ProductSpec[];
  origin: ProductOrigin | null;
  created_at: string;
  updated_at: string;
}

export interface CreateProductPayload {
  slug: string;
  name: string;
  category: string;
  grade?: string | null;
  image: string;
  images?: string[];
  description_fr: string;
  description_en: string;
  certifications?: string[];
  in_stock?: boolean;
  min_order_kg?: string | null;
  sort_order?: number;
  specifications?: ProductSpec[];
  origin?: ProductOrigin | null;
}

export type UpdateProductPayload = Partial<CreateProductPayload>;

export const adminProductsApi = {
  getAll: () => adminRequest<Product[]>('/products'),
  getOne: (id: string) => adminRequest<Product>(`/products/${id}`),
  create: (data: CreateProductPayload) =>
    adminRequest<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateProductPayload) =>
    adminRequest<Product>(`/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    adminRequest<{ id: string }>(`/products/${id}`, {
      method: 'DELETE',
    }),
  uploadImage: (file: File) => adminUpload<{ path: string }>('/products/upload', file),
};

export interface Category {
  id: string;
  label_fr: string;
  label_en: string;
  description_fr: string | null;
  description_en: string | null;
  story_fr: string | null;
  story_en: string | null;
  image: string | null;
  sort_order: number;
  created_at: string;
}

export interface CreateCategoryPayload {
  id: string;
  label_fr: string;
  label_en: string;
  description_fr?: string | null;
  description_en?: string | null;
  story_fr?: string | null;
  story_en?: string | null;
  image?: string | null;
  sort_order?: number;
}

export type UpdateCategoryPayload = Partial<Omit<CreateCategoryPayload, 'id'>>;

export const adminCategoriesApi = {
  getAll: () => adminRequest<Category[]>('/categories'),
  getOne: (id: string) => adminRequest<Category>(`/categories/${id}`),
  create: (data: CreateCategoryPayload) =>
    adminRequest<Category>('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateCategoryPayload) =>
    adminRequest<Category>(`/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    adminRequest<{ id: string }>(`/categories/${id}`, {
      method: 'DELETE',
    }),
};
